import React, { useState, useEffect } from 'react';
import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonButton,
  IonContent,
  IonList,
  IonItem,
  IonLabel,
  IonCheckbox,
  IonSpinner,
  IonText,
  IonChip,
  IonIcon,
  IonInput,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonBadge,
} from '@ionic/react';
import { closeOutline, cartOutline, checkmarkCircleOutline } from 'ionicons/icons';
import { saleService, fulfillmentService } from '../services/api';
import { Ticket, Sale } from '../types';
import { getCurrencySymbol } from '../utils/currencyConstants';
import { format } from 'date-fns';
import { formatSplitDisplay } from '../utils/formatUtils';
import { eventBus, EVENTS } from '../utils/eventBus';

interface InventoryFulfillmentModalProps {
  isOpen: boolean;
  onDismiss: () => void;
  ticket: Ticket | null;
  onSuccess: () => void;
}

interface SaleWithQuantity {
  sale: Sale;
  quantityToFulfill: number;
}

const InventoryFulfillmentModal: React.FC<InventoryFulfillmentModalProps> = ({
  isOpen,
  onDismiss,
  ticket,
  onSuccess,
}) => {
  const [unfulfilledSales, setUnfulfilledSales] = useState<Sale[]>([]);
  const [selectedSales, setSelectedSales] = useState<SaleWithQuantity[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fulfillmentDetails, setFulfillmentDetails] = useState<any>(null);

  useEffect(() => {
    if (isOpen && ticket) {
      // Clear previous selections when modal opens
      setSelectedSales([]);
      setError(null);
      loadUnfulfilledSales();
      loadFulfillmentDetails();
    } else if (!isOpen) {
      // Clear state when modal closes
      setSelectedSales([]);
      setUnfulfilledSales([]);
      setError(null);
      setFulfillmentDetails(null);
    }
  }, [isOpen, ticket]);

  const loadUnfulfilledSales = async () => {
    if (!ticket || !ticket.eventId) return;

    try {
      setLoading(true);
      const eventId = typeof ticket.eventId === 'string' ? ticket.eventId : ticket.eventId._id;
      const allSales = await saleService.getAll({ eventId });
      
      // Filter for unfulfilled sales (regardless of category)
      const unfulfilled = allSales.filter(sale => {
        const hasTickets = sale.ticketIds && sale.ticketIds.length > 0;
        return sale.status === 'confirmed' && !hasTickets;
      });

      // Sort sales by assignment efficiency for better user guidance
      const availableQty = ticket.quantity - (ticket.soldQuantity || 0);
      const sortedSales = unfulfilled.sort((a, b) => {
        const aQuantity = a.quantity || 1;
        const bQuantity = b.quantity || 1;
        
        // Priority 1: Category matches
        const aCategoryMatch = a.category === ticket.category ? 1 : 0;
        const bCategoryMatch = b.category === ticket.category ? 1 : 0;
        if (aCategoryMatch !== bCategoryMatch) return bCategoryMatch - aCategoryMatch;
        
        // Priority 2: Exact matches (perfect fit)
        const aExactMatch = aQuantity === availableQty ? 1 : 0;
        const bExactMatch = bQuantity === availableQty ? 1 : 0;
        if (aExactMatch !== bExactMatch) return bExactMatch - aExactMatch;
        
        // Priority 3: Can be fully fulfilled
        const aCanFulfill = aQuantity <= availableQty ? 1 : 0;
        const bCanFulfill = bQuantity <= availableQty ? 1 : 0;
        if (aCanFulfill !== bCanFulfill) return bCanFulfill - aCanFulfill;
        
        // Priority 4: Minimal waste (largest quantity that fits)
        if (aQuantity <= availableQty && bQuantity <= availableQty) {
          return bQuantity - aQuantity; // Larger quantities first
        }
        
        // Priority 5: Newest sales first (most recent activity)
        return new Date(b.saleDate).getTime() - new Date(a.saleDate).getTime();
      });
      
      setUnfulfilledSales(sortedSales);
    } catch (err: any) {
      setError(err.message || 'Failed to load sales');
    } finally {
      setLoading(false);
    }
  };

  const loadFulfillmentDetails = async () => {
    if (!ticket || !ticket._id) return;

    try {
      const details = await fulfillmentService.getTicketFulfillment(ticket._id);
      setFulfillmentDetails(details);
    } catch (err) {
      console.error('Failed to load fulfillment details:', err);
    }
  };

  const toggleSaleSelection = (sale: Sale) => {
    const existing = selectedSales.find(s => s.sale._id === sale._id);
    
    if (existing) {
      setSelectedSales(selectedSales.filter(s => s.sale._id !== sale._id));
    } else {
      const maxQuantity = Math.min(
        sale.quantity || 1,
        getAvailableQuantity()
      );
      
      if (maxQuantity > 0) {
        setSelectedSales([...selectedSales, { sale, quantityToFulfill: maxQuantity }]);
      }
    }
  };

  const updateQuantity = (saleId: string, quantity: number) => {
    setSelectedSales(selectedSales.map(s => 
      s.sale._id === saleId ? { ...s, quantityToFulfill: quantity } : s
    ));
  };

  const getAvailableQuantity = () => {
    if (!ticket) return 0;
    const totalSelected = selectedSales.reduce((sum, s) => sum + s.quantityToFulfill, 0);
    return (ticket.quantity - (ticket.soldQuantity || 0)) - totalSelected;
  };

  const handleLink = async () => {
    if (selectedSales.length === 0 || !ticket) return;

    try {
      setLoading(true);
      setError(null);
      
      // Link each selected sale
      for (const { sale, quantityToFulfill } of selectedSales) {
        await fulfillmentService.linkSaleToInventory(
          sale._id || sale.id || '',
          ticket._id || ticket.id || '',
          quantityToFulfill
        );
      }

      onSuccess();
      onDismiss();
      
      // Notify inventory to refresh
      eventBus.emit(EVENTS.REFRESH_TICKETS);
      // Also notify sales to refresh
      eventBus.emit(EVENTS.REFRESH_SALES);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to link sales');
    } finally {
      setLoading(false);
    }
  };

  const availableQuantity = ticket ? 
    ticket.quantity - (ticket.soldQuantity || 0) : 0;

  return (
    <IonModal isOpen={isOpen} onDidDismiss={onDismiss}>
      <IonHeader>
        <IonToolbar>
          <IonTitle>Link Inventory to Sales</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={onDismiss}>
              <IonIcon icon={closeOutline} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        {loading && !unfulfilledSales.length ? (
          <div className="flex justify-center py-8">
            <IonSpinner />
          </div>
        ) : (
          <>
            {/* Assignment Guide */}
            <div className="bg-blue-50 dark:bg-blue-900/30 p-4 rounded-lg mb-4">
              <h3 className="text-sm font-semibold text-blue-800 dark:text-blue-200 mb-2">💡 Assignment Tips</h3>
              <p className="text-sm text-blue-700 dark:text-blue-300">
                The ⭐ RECOMMENDED sale minimizes inventory splitting. Perfect fits use all tickets, 
                while optimal choices leave minimal remaining inventory.
              </p>
            </div>

            {/* Ticket Details */}
            <IonCard>
              <IonCardHeader>
                <IonCardTitle>Inventory Details</IonCardTitle>
              </IonCardHeader>
              <IonCardContent>
                <div className="space-y-2">
                  <p><strong>Category:</strong> {ticket?.category}</p>
                  <p><strong>Section:</strong> {ticket?.section || 'General'}</p>
                  {ticket?.block && <p><strong>Block:</strong> {ticket.block}</p>}
                  <p><strong>Split:</strong> {formatSplitDisplay(ticket?.split || 'together')}</p>
                  <p className="text-green-600 dark:text-green-400">
                    <strong>Available:</strong> {availableQuantity} tickets
                  </p>
                  {fulfillmentDetails && fulfillmentDetails.totalAllocated > 0 && (
                    <p className="text-yellow-600 dark:text-yellow-400">
                      <strong>Already Allocated:</strong> {fulfillmentDetails.totalAllocated}
                    </p>
                  )}
                </div>
              </IonCardContent>
            </IonCard>

            {error && (
              <div className="bg-red-100 dark:bg-red-900/30 p-4 rounded-lg mb-4">
                <IonText color="danger">{error}</IonText>
              </div>
            )}

            {availableQuantity <= 0 ? (
              <div className="text-center py-8">
                <IonIcon icon={checkmarkCircleOutline} className="text-4xl text-green-600" />
                <p className="mt-2 text-green-600">All inventory has been allocated!</p>
              </div>
            ) : (
              <>
                {/* Summary */}
                <div className="mb-4 p-4 bg-primary-50 dark:bg-primary-900/30 rounded-lg">
                  <p className="font-medium">
                    Selected: {selectedSales.reduce((sum, s) => sum + s.quantityToFulfill, 0)} tickets
                    for {selectedSales.length} sale(s)
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Remaining: {getAvailableQuantity()} tickets
                  </p>
                </div>

                {/* Unfulfilled Sales */}
                <h3 className="text-lg font-medium mb-2">Unfulfilled Sales</h3>
                {unfulfilledSales.length === 0 ? (
                  <p className="text-gray-500">No unfulfilled sales for this event</p>
                ) : (
                  <IonList>
                    {unfulfilledSales.map((sale, index) => {
                      const saleId = sale._id || sale.id || '';
                      const isSelected = selectedSales.some(s => s.sale._id === saleId);
                      const selectedItem = selectedSales.find(s => s.sale._id === saleId);
                      const isCategoryMatch = sale.category === ticket?.category;
                      const saleQuantity = sale.quantity || 1;
                      const availableQty = ticket ? ticket.quantity - (ticket.soldQuantity || 0) : 0;
                      const isExactMatch = saleQuantity === availableQty;
                      const canFulfillFully = saleQuantity <= availableQty;
                      const waste = canFulfillFully ? availableQty - saleQuantity : 0;
                      const isOptimal = index === 0; // First item after sorting is most optimal
                      
                      // Generate efficiency indicator
                      const getEfficiencyInfo = () => {
                        if (isExactMatch) {
                          return { label: 'Perfect Fit', color: 'success', icon: checkmarkCircleOutline };
                        } else if (canFulfillFully && waste === 1) {
                          return { label: `Will leave 1 single`, color: 'warning', icon: cartOutline };
                        } else if (canFulfillFully && waste <= 3) {
                          return { label: `Will leave ${waste} tickets`, color: 'medium', icon: cartOutline };
                        } else if (canFulfillFully) {
                          return { label: `Will leave ${waste} tickets`, color: 'dark', icon: cartOutline };
                        } else {
                          return { label: `Partial (needs ${saleQuantity}, have ${availableQty})`, color: 'danger', icon: cartOutline };
                        }
                      };
                      
                      const efficiency = getEfficiencyInfo();
                      
                      return (
                        <IonItem key={saleId} className={isOptimal ? 'border-l-4 border-green-500 bg-green-50 dark:bg-green-900/20' : ''}>
                          <IonCheckbox
                            slot="start"
                            checked={isSelected}
                            onIonChange={() => toggleSaleSelection(sale)}
                          />
                          <IonLabel>
                            <h4 className="font-medium">
                              {sale.buyerName} - {format(new Date(sale.saleDate), 'MMM dd')}
                              {isOptimal && <span className="ml-2 text-green-600 text-sm font-bold">⭐ RECOMMENDED</span>}
                            </h4>
                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                              <IonChip color="primary">
                                <IonIcon icon={cartOutline} />
                                <IonLabel>{sale.quantity || 1} needed</IonLabel>
                              </IonChip>
                              <IonChip color={efficiency.color}>
                                <IonIcon icon={efficiency.icon} />
                                <IonLabel>{efficiency.label}</IonLabel>
                              </IonChip>
                              <span className="text-sm">
                                {sale.category || 'Any'} - {formatSplitDisplay(sale.split || 'together')}
                              </span>
                              {!isCategoryMatch && (
                                <IonBadge color="warning">Different Category</IonBadge>
                              )}
                            </div>
                            {sale.orderNumber && (
                              <p className="text-sm text-gray-500 mt-1">
                                Order: {sale.orderNumber}
                              </p>
                            )}
                          </IonLabel>
                          {isSelected && selectedItem && (
                            <IonInput
                              type="number"
                              value={selectedItem.quantityToFulfill}
                              onIonChange={(e) => updateQuantity(saleId, parseInt(e.detail.value!) || 1)}
                              min={1}
                              max={Math.min(sale.quantity || 1, getAvailableQuantity() + selectedItem.quantityToFulfill)}
                              className="max-w-24"
                            />
                          )}
                        </IonItem>
                      );
                    })}
                  </IonList>
                )}

                {/* Action Buttons */}
                <div className="flex gap-2 mt-4">
                  <IonButton 
                    expand="block" 
                    onClick={handleLink}
                    disabled={selectedSales.length === 0 || loading}
                  >
                    {loading ? <IonSpinner /> : `Link ${selectedSales.length} Sale(s)`}
                  </IonButton>
                  <IonButton 
                    expand="block" 
                    fill="outline" 
                    onClick={onDismiss}
                  >
                    Cancel
                  </IonButton>
                </div>
              </>
            )}
          </>
        )}
      </IonContent>
    </IonModal>
  );
};

export default InventoryFulfillmentModal;